/**
 * ForkPromptModal — Phase 2 (single-node fork) + Phase 3 (orchestration fork).
 *
 * Lets the user edit a node's prompt (and optionally context JSON) and re-run
 * either:
 *   - Just this node (default — invokes the same agent with the new prompt), or
 *   - The whole orchestration with this prompt overridden (only available when
 *     the source node is a sub-task of an orchestration).
 *
 * After the fork completes, shows a side-by-side diff of original vs new output
 * with a stats strip (Δ tokens, Δ cost, Δ duration).
 */

import { useState, useEffect } from 'react'
import {
  XMarkIcon,
  PlayIcon,
  ArrowPathIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import toast from 'react-hot-toast'
import { apiClient } from '@/services/api'
import Button from '@/components/common/Button'

export interface ForkPromptSource {
  taskId: string
  agentName: string
  prompt: string
  output?: string
  totalTokens?: number
  costUsd?: number
  durationMs?: number
  /** request_id of the parent execution (the row in execution_log).
   *  For sub-tasks of an orchestration this is the parent orchestration's
   *  request_id when caller wants orchestration-level fork. */
  parentRequestId?: string
  /** If true, the "Fork whole orchestration" toggle is shown. */
  isOrchestrationSubtask?: boolean
}

interface ForkPromptModalProps {
  source: ForkPromptSource | null
  onClose: () => void
  onForkComplete?: (result: ForkResult) => void
}

interface ForkResult {
  success: boolean
  output: string
  totalTokens?: number
  costUsd?: number
  durationMs?: number
  modelUsed?: string
  forkedFromRequestId?: string
  newRequestId?: string
  isOrchestrationFork?: boolean
}

export default function ForkPromptModal({
  source,
  onClose,
  onForkComplete,
}: ForkPromptModalProps) {
  const [editedPrompt, setEditedPrompt] = useState('')
  const [contextJson, setContextJson] = useState('{}')
  const [forkOrchestration, setForkOrchestration] = useState(false)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<ForkResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (source) {
      setEditedPrompt(source.prompt || '')
      setContextJson('{}')
      setResult(null)
      setError(null)
      setForkOrchestration(false)
    }
  }, [source])

  if (!source) return null

  const handleRun = async () => {
    if (!editedPrompt.trim()) {
      toast.error('Prompt cannot be empty')
      return
    }
    let parsedCtx: any = {}
    try { parsedCtx = contextJson.trim() ? JSON.parse(contextJson) : {} }
    catch { toast.error('Context must be valid JSON'); return }

    setRunning(true)
    setError(null)

    try {
      let newOutput = ''
      let newTokens: number | undefined
      let newCost: number | undefined
      let newDuration: number | undefined
      let newModel: string | undefined
      let newReqId: string | undefined

      if (forkOrchestration && source.parentRequestId) {
        // Phase 3: fork the whole orchestration with this sub-task overridden
        const resp = await (apiClient as any).client.post(
          `/api/v1/executions/history/${source.parentRequestId}/fork-orchestration`,
          { prompt_overrides: { [source.taskId]: editedPrompt } }
        )
        const r = resp.data?.result || {}
        newOutput = r.content || (r.result?.output) || JSON.stringify(r).slice(0, 1000)
        newTokens = r.metadata?.total_tokens
        newCost = r.metadata?.total_cost_usd
        newDuration = r.metadata?.duration_ms
        newReqId = r.request_id
      } else {
        // Phase 2: single-node fork — invoke the same agent with the edited prompt
        const ctx = { ...parsedCtx, _forked_from: source.parentRequestId || source.taskId }
        const resp = await (apiClient as any).client.post(
          `/api/v1/agents/${source.agentName}/invoke`,
          { prompt: editedPrompt, context: ctx }
        )
        const r = resp.data || {}
        newOutput = r.content || (r.result?.output) || JSON.stringify(r).slice(0, 1000)
        newTokens = r.metadata?.total_tokens
        newCost = r.metadata?.total_cost_usd
        newDuration = r.metadata?.duration_ms || r.duration_ms
        newModel = r.metadata?.model_used
        newReqId = r.request_id
      }

      const forkResult: ForkResult = {
        success: true,
        output: newOutput,
        totalTokens: newTokens,
        costUsd: newCost,
        durationMs: newDuration,
        modelUsed: newModel,
        forkedFromRequestId: source.parentRequestId,
        newRequestId: newReqId,
        isOrchestrationFork: forkOrchestration,
      }
      setResult(forkResult)
      onForkComplete?.(forkResult)
      toast.success('Fork complete')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Fork failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setRunning(false)
    }
  }

  const delta = (newVal?: number, oldVal?: number) => {
    if (newVal == null || oldVal == null) return null
    const d = newVal - oldVal
    if (d === 0) return null
    const sign = d > 0 ? '+' : ''
    return `${sign}${typeof newVal === 'number' && newVal % 1 !== 0 ? d.toFixed(4) : d.toLocaleString()}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <CodeBracketIcon className="w-5 h-5 text-primary" />
              Fork &amp; Edit Prompt
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Re-run <span className="font-mono">{source.agentName}</span> with an edited prompt — original stays in History.
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Prompt editor */}
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
              Prompt (edit, then Run)
            </label>
            <textarea
              value={editedPrompt}
              onChange={e => setEditedPrompt(e.target.value)}
              rows={8}
              className="w-full text-xs px-3 py-2 rounded-md border border-border bg-card font-mono resize-y"
            />
          </div>

          {/* Context JSON (optional) */}
          <details>
            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
              Context (JSON) — optional, will be merged with default fork context
            </summary>
            <textarea
              value={contextJson}
              onChange={e => setContextJson(e.target.value)}
              rows={3}
              className="mt-2 w-full text-xs px-2 py-2 rounded-md border border-border bg-card font-mono resize-y"
            />
          </details>

          {/* Orchestration fork toggle */}
          {source.isOrchestrationSubtask && source.parentRequestId && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={forkOrchestration}
                onChange={e => setForkOrchestration(e.target.checked)}
                className="rounded border-border"
              />
              <span className="text-foreground">
                Fork the <strong>whole orchestration</strong> with this prompt overridden
              </span>
              <span className="text-muted-foreground">(reuses parent's TaskDAG)</span>
            </label>
          )}

          {error && (
            <div className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded p-2">
              {error}
            </div>
          )}

          {/* Result diff */}
          {result && (
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Fork Result</p>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  {result.durationMs != null && (
                    <span>
                      {(result.durationMs / 1000).toFixed(1)}s
                      {delta(result.durationMs, source.durationMs) && (
                        <span className={clsx('ml-1', (result.durationMs! - (source.durationMs || 0)) > 0 ? 'text-amber-500' : 'text-emerald-500')}>
                          ({delta(result.durationMs, source.durationMs)}ms)
                        </span>
                      )}
                    </span>
                  )}
                  {result.totalTokens != null && (
                    <span>
                      {result.totalTokens.toLocaleString()} tok
                      {delta(result.totalTokens, source.totalTokens) && (
                        <span className={clsx('ml-1', (result.totalTokens! - (source.totalTokens || 0)) > 0 ? 'text-amber-500' : 'text-emerald-500')}>
                          ({delta(result.totalTokens, source.totalTokens)})
                        </span>
                      )}
                    </span>
                  )}
                  {result.costUsd != null && (
                    <span>
                      ${result.costUsd.toFixed(4)}
                      {delta(result.costUsd, source.costUsd) && (
                        <span className={clsx('ml-1', (result.costUsd! - (source.costUsd || 0)) > 0 ? 'text-amber-500' : 'text-emerald-500')}>
                          (Δ${delta(result.costUsd, source.costUsd)})
                        </span>
                      )}
                    </span>
                  )}
                  {result.modelUsed && <span className="px-1.5 py-0.5 bg-muted rounded">{result.modelUsed}</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Original Output</p>
                  <pre className="text-xs bg-muted/30 rounded p-2 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono border border-border">
                    {source.output || '(no output)'}
                  </pre>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase mb-1">New Output</p>
                  <pre className="text-xs bg-emerald-500/5 border border-emerald-500/20 rounded p-2 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono">
                    {result.output || '(no output)'}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={running}>Close</Button>
          <Button onClick={handleRun} disabled={running || !editedPrompt.trim()}>
            {running ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
            <span className="ml-1.5">
              {running ? 'Running…' : forkOrchestration ? 'Fork Orchestration' : 'Run Fork'}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}
